import { useQuery } from "@tanstack/react-query"
import { auth } from "@/lib/firebase"
import { Document } from "@/types"
import { useEffect } from "react"
import { useUpload } from "@/contexts/UploadContext"

export interface PaginatedDocumentsResponse {
	documents: Document[]
	total: number
	page: number
	limit: number
	total_pages: number
	query?: string
}

export interface DocumentSearchResult extends Document {
	relevance_to_search?: "Exact Match" | "Partial Match" | "Weak Match"
}

export type SortField = "name" | "type" | "size" | "created_at" | "relevance"
export type SortDirection = "asc" | "desc"

interface UsePaginatedDocumentsParams {
	caseId: string | null | undefined
	page?: number
	limit?: number
	relevantOnly?: boolean
	sortBy?: SortField
	sortDirection?: SortDirection
	searchQuery?: string
	includeIrrelevant?: boolean
}

const fetchPaginatedDocuments = async ({
	caseId,
	page = 1,
	limit = 20,
	relevantOnly = false,
	sortBy = "name",
	sortDirection = "asc",
	searchQuery,
	includeIrrelevant = false,
}: {
	caseId: string
	page: number
	limit: number
	relevantOnly: boolean
	sortBy: SortField
	sortDirection: SortDirection
	searchQuery?: string
	includeIrrelevant?: boolean
}): Promise<PaginatedDocumentsResponse> => {
	const user = auth.currentUser
	if (!user) {
		throw new Error("User not found.")
	}
	const token = await user.getIdToken()

	// Determine which endpoint to use
	const isSearch = !!searchQuery

	const params = new URLSearchParams({
		page: page.toString(),
		limit: limit.toString(),
		sort_by: sortBy,
		sort_direction: sortDirection,
	})

	if (isSearch) {
		params.append("query", searchQuery)
		params.append("include_irrelevant", includeIrrelevant.toString())
	} else {
		params.append("relevant_only", relevantOnly.toString())
	}

	const endpoint = isSearch
		? `${
				import.meta.env.VITE_API_URL
		  }/api/cases/${caseId}/documents/search?${params}`
		: `${import.meta.env.VITE_API_URL}/api/cases/${caseId}/documents/?${params}`

	const response = await fetch(endpoint, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})

	if (!response.ok) {
		throw new Error(`HTTP error! status: ${response.status}`)
	}

	const data = await response.json()
	return data
}

/**
 * Hook to fetch paginated documents for a case (with optional search)
 */
export const usePaginatedDocuments = ({
	caseId,
	page = 1,
	limit = 20,
	relevantOnly = false,
	sortBy = "name",
	sortDirection = "asc",
	searchQuery,
	includeIrrelevant = false,
}: UsePaginatedDocumentsParams) => {
	const { uploadState } = useUpload()
	
	const query = useQuery<PaginatedDocumentsResponse, Error>({
		queryKey: [
			"documents",
			caseId,
			page,
			limit,
			relevantOnly,
			sortBy,
			sortDirection,
			searchQuery,
			includeIrrelevant,
		],
		queryFn: () =>
			fetchPaginatedDocuments({
				caseId: caseId!,
				page,
				limit,
				relevantOnly,
				sortBy,
				sortDirection,
				searchQuery,
				includeIrrelevant,
			}),
		enabled: !!caseId,
		staleTime: 5 * 60 * 1000, // 5 minutes
		placeholderData: (previousData, previousQuery) => {
			if (!previousData || !previousQuery) return undefined
			
			// Extract the relevant parts of the query key
			const [, , prevPage, , , prevSortBy, prevSortDirection, prevSearchQuery, prevIncludeIrrelevant] = previousQuery.queryKey as any[]
			
			// Only use placeholder data if search query and includeIrrelevant are the same
			// but page, sorting, or other parameters changed
			const sameSearchParams = prevSearchQuery === searchQuery && prevIncludeIrrelevant === includeIrrelevant
			const differentOtherParams = prevPage !== page || prevSortBy !== sortBy || prevSortDirection !== sortDirection
			
			if (sameSearchParams && differentOtherParams) {
				return previousData
			}
			
			return undefined
		},
	})
	
	// Refetch when uploads are completed
	useEffect(() => {
		const completedUploads = uploadState.tasks.filter(
			task => task.caseId === caseId && task.status === "completed"
		)
		
		if (completedUploads.length > 0) {
			query.refetch()
		}
	}, [uploadState.tasks, caseId, query])
	
	return query
}
